'use client';

import React, { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Sparkles,
  Check,
  ArrowRight,
  ShieldAlert,
  Zap,
  Lock,
  Building2,
  Users,
  Ticket,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { subscriptionApi, getApiErrorMessage } from '@/lib/api';
import {
  PLANS_CONFIG,
  type PlanKey,
  getPlan,
  ACTIVITY_TYPE_LABELS,
  FEATURE_LABELS,
  type FeatureKey,
} from '@/config/plans.config';

export interface UpgradeRequiredDetail {
  code?: string;
  reason?: 'ACTIVITY_TYPE' | 'FEATURE' | 'PARTICIPANT_LIMIT' | 'PAID_EVENTS';
  currentPlan?: string;
  requiredPlan?: string;
  feature?: string;
  activityType?: string;
  limits?: {
    current_plan_limit?: number | null;
    required_plan_limit?: number | null;
  };
  message?: string;
}

export function triggerPlanUpgrade(detail: UpgradeRequiredDetail) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('plan:upgrade-required', { detail })
    );
  }
}

export function triggerCustomPlanRequest(initialValues?: Record<string, unknown>) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent('plan:custom-request', { detail: initialValues })
    );
  }
}

export function UpgradeModal() {
  const router = useRouter();
  const [isOpen, setIsOpen] = useState(false);
  const [data, setData] = useState<UpgradeRequiredDetail | null>(null);
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    const handleUpgradeRequired = (event: Event) => {
      const detail = (event as CustomEvent<UpgradeRequiredDetail>).detail;
      setData(detail || {});
      setIsOpen(true);
    };

    window.addEventListener('plan:upgrade-required', handleUpgradeRequired);
    return () => {
      window.removeEventListener('plan:upgrade-required', handleUpgradeRequired);
    };
  }, []);

  const close = () => {
    if (upgrading) return;
    setIsOpen(false);
  };

  if (!isOpen || !data) return null;

  const PLAN_RANK: Record<string, number> = {
    free: 0,
    bronze: 1,
    silver: 2,
    custom: 3,
  };

  const currentKey = (data.currentPlan || 'free').toLowerCase() as PlanKey;
  let targetKey: PlanKey = (data.requiredPlan || 'silver').toLowerCase() as PlanKey;
  if (!['free', 'bronze', 'silver', 'custom'].includes(targetKey)) {
    targetKey = 'silver';
  }

  const currentRank = PLAN_RANK[currentKey] ?? 0;
  const targetRank = PLAN_RANK[targetKey] ?? 1;

  // RULE: A higher-tier organization must NEVER be told to downgrade
  if (targetRank <= currentRank) {
    if (currentRank < PLAN_RANK.silver) {
      targetKey = 'silver';
    } else {
      targetKey = 'custom';
    }
  }

  const currentPlan = getPlan(currentKey);
  const targetPlan = getPlan(targetKey);

  // Generate friendly headline & context based on reason
  const isExplicitAdminBlock =
    data.code === 'ORGANIZATION_FEATURE_DISABLED' ||
    data.code === 'FEATURE_RESTRICTED' ||
    Boolean(data.message && data.message.toLowerCase().includes('disabled for your organization'));

  let reasonTitle = 'Upgrade Your Organization Plan';
  let reasonDescription = `To access this feature, please upgrade your organization plan from ${currentPlan.name} to ${targetPlan.name}.`;

  if (data.reason === 'ACTIVITY_TYPE') {
    const actName =
      (data.activityType && ACTIVITY_TYPE_LABELS[data.activityType]) ||
      (data.feature && ACTIVITY_TYPE_LABELS[data.feature]) ||
      'This activity format';
    reasonTitle = `${actName} Requires ${targetPlan.name}`;
    reasonDescription = `${actName} templates are exclusively available on the ${targetPlan.name} tier and above.`;
  } else if (data.reason === 'FEATURE') {
    const featKey = (data.feature || '') as FeatureKey;
    const featName = FEATURE_LABELS[featKey]?.name || data.feature || 'This module';

    if (isExplicitAdminBlock) {
      reasonTitle = `${featName} Access Restricted`;
      reasonDescription =
        data.message ||
        `${featName} is disabled for your organization by an administrator. Please contact your organization administrator or support to enable it.`;
    } else {
      reasonTitle = `Unlock ${featName}`;
      reasonDescription =
        data.message ||
        `${featName} is not included in the ${currentPlan.name} plan. Upgrade to ${targetPlan.name} to activate it instantly.`;
    }
  } else if (data.reason === 'PARTICIPANT_LIMIT') {
    const currLimit = data.limits?.current_plan_limit ?? currentPlan.maxParticipantsPerActivity;
    const reqLimit = data.limits?.required_plan_limit ?? targetPlan.maxParticipantsPerActivity;
    reasonTitle = 'Participant Limit Reached';
    reasonDescription = `Your current plan caps activities at ${currLimit ?? 100} participants. Upgrade to ${targetPlan.name} to host up to ${reqLimit ? reqLimit.toLocaleString() : 'unlimited'} attendees.`;
  } else if (data.reason === 'PAID_EVENTS') {
    reasonTitle = 'Paid Events Require Higher Tier';
    reasonDescription = `Paid ticket sales and paid registrations are only available on active paid tiers. Upgrade to ${targetPlan.name} to start accepting paid entries.`;
  }

  const handleUpgrade = async () => {
    if (targetKey === 'custom') {
      close();
      triggerCustomPlanRequest({
        expected_participants: 3500,
        requirements: `Requesting Custom tier plan for expanded activity limits and enterprise features.`,
      });
      return;
    }

    setUpgrading(true);
    try {
      await subscriptionApi.upgrade({ plan_key: targetKey });
      toast.success(`Successfully upgraded to ${targetPlan.name}!`);
      setIsOpen(false);
      // Reload current route so new entitlements apply immediately
      router.refresh();
      window.dispatchEvent(new CustomEvent('plan:updated', { detail: { plan: targetKey } }));
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to upgrade plan. Please try again.'));
    } finally {
      setUpgrading(false);
    }
  };

  const handleOpenCustom = () => {
    close();
    triggerCustomPlanRequest();
  };

  const handleComparePlans = () => {
    close();
    router.push('/dashboard/settings/subscription');
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
          className="fixed inset-0 bg-black/65 backdrop-blur-sm transition-opacity"
        />

        {/* Modal Card */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0, y: 15 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 15 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-slate-700 bg-gradient-to-b from-slate-900 to-slate-950 p-6 sm:p-8 text-white shadow-2xl"
          role="dialog"
          aria-modal="true"
        >
          {/* Ambient Glow */}
          <div className="absolute -right-20 -top-20 h-56 w-56 rounded-full bg-emerald-500/15 blur-3xl" />
          <div className="absolute -left-20 -bottom-20 h-56 w-56 rounded-full bg-cyan-500/10 blur-3xl" />

          {/* Close button */}
          <button
            type="button"
            onClick={close}
            className="absolute right-5 top-5 rounded-full p-2 text-slate-400 hover:bg-white/10 hover:text-white transition"
            aria-label="Close upgrade dialog"
          >
            <X className="h-5 w-5" />
          </button>

          {/* Top Badge */}
          <div className="flex items-center gap-2 mb-4">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-400">
              <Sparkles className="h-3.5 w-3.5" />
              Plan Upgrade
            </span>
            <span className="rounded-full bg-slate-800 px-2.5 py-1 text-[11px] font-medium text-slate-400">
              Current: <strong className="text-white capitalize">{currentPlan.name}</strong>
            </span>
          </div>

          {/* Headline */}
          <h2 className="text-2xl font-bold tracking-tight text-white leading-snug">
            {reasonTitle}
          </h2>
          <p className="mt-2 text-sm text-slate-300 leading-relaxed">
            {reasonDescription}
          </p>

          {/* Highlights Card */}
          {isExplicitAdminBlock ? (
            <div className="mt-5 rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-amber-400 shrink-0 mt-0.5" />
                <div className="text-xs sm:text-sm text-slate-300 space-y-1">
                  <p className="font-semibold text-white">Administrative Override Active</p>
                  <p>
                    This capability has been specifically restricted for your organization. Contact your organization administrator or EnthiosHub support to review organization-level permissions.
                  </p>
                </div>
              </div>
            </div>
          ) : (
            <div className="mt-5 rounded-2xl border border-slate-800 bg-slate-900/80 p-4 sm:p-5">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-emerald-400">
                    Recommended Tier
                  </p>
                  <h3 className="text-lg font-bold text-white mt-0.5">{targetPlan.name} Plan</h3>
                </div>
                <div className="text-right">
                  <span className="text-xl font-bold text-white">
                    {targetKey === 'custom'
                      ? 'Custom'
                      : targetKey === 'free' || Number(targetPlan.commissionRate) === 0
                      ? 'Free'
                      : `${Number(targetPlan.commissionRate).toFixed(2)}%`}
                  </span>
                  <span className="block text-[10px] text-slate-400 uppercase tracking-wide">
                    {targetKey === 'custom'
                      ? 'Negotiated rate'
                      : targetKey === 'free' || Number(targetPlan.commissionRate) === 0
                      ? 'Zero platform fee'
                      : `+ ${targetPlan.gstRate ?? 18}% GST`}
                  </span>
                </div>
              </div>

              {/* Feature bullets for target plan */}
              <ul className="mt-3 space-y-2 text-xs sm:text-sm text-slate-300">
                <li className="flex items-center gap-2.5">
                  <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                  <span>
                    Host up to{' '}
                    <strong className="text-white">
                      {targetPlan.maxParticipantsPerActivity
                        ? targetPlan.maxParticipantsPerActivity.toLocaleString()
                        : 'Unlimited'}
                    </strong>{' '}
                    participants per activity
                  </span>
                </li>

                {targetPlan.paidEventsAllowed && (
                  <li className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                    <span>Sell paid tickets and registrations directly</span>
                  </li>
                )}

                {targetKey === 'silver' && (
                  <>
                    <li className="flex items-center gap-2.5">
                      <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                      <span>All activity templates: Hackathons, MUNs, Conferences & Quizzes</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                      <span>Team registration, automated certificates & jury scoring</span>
                    </li>
                  </>
                )}

                {targetKey === 'bronze' && (
                  <li className="flex items-center gap-2.5">
                    <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                    <span>QR ticketing, attendee check-in desk, and direct communications</span>
                  </li>
                )}

                {targetKey === 'custom' && (
                  <>
                    <li className="flex items-center gap-2.5">
                      <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                      <span>High-volume participant limits tailored for your university or brand</span>
                    </li>
                    <li className="flex items-center gap-2.5">
                      <Check className="h-4 w-4 shrink-0 text-emerald-400" />
                      <span>Dedicated account manager and bespoke platform integrations</span>
                    </li>
                  </>
                )}
              </ul>
            </div>
          )}

          {/* Action Buttons */}
          <div className="mt-6 flex flex-col gap-2.5">
            {isExplicitAdminBlock ? (
              <button
                type="button"
                onClick={close}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-slate-800 px-5 py-3 text-sm font-bold text-white shadow-lg hover:bg-slate-700 active:scale-[0.99] transition"
              >
                Understood
              </button>
            ) : targetKey === 'custom' ? (
              <button
                type="button"
                onClick={handleOpenCustom}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-emerald-900/30 hover:brightness-110 active:scale-[0.99] transition"
              >
                <Building2 className="h-4 w-4" />
                Contact Us for Custom Plan
              </button>
            ) : (
              <button
                type="button"
                disabled={upgrading}
                onClick={handleUpgrade}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-emerald-500 px-5 py-3 text-sm font-bold text-slate-950 shadow-lg shadow-emerald-500/25 hover:bg-emerald-400 active:scale-[0.99] transition disabled:opacity-50"
              >
                {upgrading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-slate-950 border-t-transparent" />
                    Upgrading to {targetPlan.name}…
                  </>
                ) : (
                  <>
                    <Zap className="h-4 w-4 fill-current" />
                    Upgrade to {targetPlan.name} Now
                  </>
                )}
              </button>
            )}

            <div className="flex items-center justify-between gap-2 pt-1">
              {!isExplicitAdminBlock && (
                <button
                  type="button"
                  onClick={handleComparePlans}
                  className="text-xs font-semibold text-slate-400 hover:text-white transition"
                >
                  Compare all 4 plans →
                </button>
              )}

              <button
                type="button"
                onClick={close}
                className="text-xs font-semibold text-slate-500 hover:text-slate-300 transition ml-auto"
              >
                Close
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
